import { Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { first, Observable, of } from 'rxjs';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { eDays } from 'src/app/modules/route-planner/constants/days.constants';
import {
  DAY,
  DAY_OF_WEEK_RADIO_GROUP_LABEL,
  DAYS_LETTERS,
  DAYS_NAMES,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Day } from 'src/app/modules/route-planner/interfaces/route-planning/day.interface';

@Component({
    selector: 'app-day-picker',
    templateUrl: './day-picker.component.html',
    styleUrls: ['./day-picker.component.scss'],
    standalone: false
})
export class DayPickerComponent extends ResizeService implements OnInit {
  @Output() onDayPicked: EventEmitter<Day> = new EventEmitter<Day>();
  @Input() selectedDay: Day;

  @ViewChildren('dayRadioBtn')
  dayRadioBtns!: QueryList<ElementRef<HTMLElement>>;

  isLastSizeMobile: boolean;
  dayFormGroup: FormGroup;
  dayText: string;
  dayOptionsList: SelectOption<Day>[] = [];
  dayOfWeekGroupAriaLabel = '';

  constructor(
    private readonly languageService: LanguageService,
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly dir?: Directionality
  ) {
    super();
    this.dayFormGroup = new FormGroup({
      dayControl: new FormControl<Day>(null),
    });
  }

  formControl: FormControl;
  dayOptions: Observable<SelectOption<Day>[]>;

  ngOnInit(): void {
    this.isLastSizeMobile = this.isMobile;
    this.formControl = this.dayFormGroup.get('dayControl') as FormControl;
    this.dayFormGroup.get('dayControl').valueChanges.subscribe((day) => {
      if (this.isMobile) {
        this.pickDay(day);
      }
    });
    this.initializeMultiLanguageTexts();

    this.dayOptions.pipe(first()).subscribe((options) => {
      const index = new Date(Date.now()).getDay() || 0;
      this.formControl.patchValue(options[index].value);

      if (!this.isMobile) {
        this.onDayPicked.emit(options[index].value);
      }
    });

    this.onResize$.subscribe(() => {
      if (
        (this.isLastSizeMobile && !this.isMobile) ||
        (!this.isLastSizeMobile && this.isMobile)
      ) {
        this.initializeMultiLanguageTexts();
        this.dayOptions.pipe(first()).subscribe((options) => {
          const index = new Date(Date.now()).getDay() || 0;
          this.formControl.patchValue(
            options.find(
              (option) => option.value.name === this.formControl.value.name
            ).value
          );
          this.isLastSizeMobile = this.isMobile;
        });
      }
    });
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      const options: SelectOption<Day>[] = [];
      Object.keys(eDays)
        .filter((key) => isNaN(Number(key)))
        .forEach((key) => {
          const dayValue = eDays[key as keyof typeof eDays];
          options.push({
            label: this.isMobile
              ? DAYS_NAMES[language.language][dayValue]
              : DAYS_LETTERS[language.language][dayValue],
            ariaLabel: DAYS_NAMES[language.language][dayValue],
            value: {
              name: key,
              value: dayValue,
            },
          });
        });

      this.dayOptionsList = options;
      this.dayOptions = of(options);
      this.dayText = DAY[language.language];
      this.dayOfWeekGroupAriaLabel =
        DAY_OF_WEEK_RADIO_GROUP_LABEL[language.language];
    });
  }

  isDaySelected(dayOption: SelectOption<Day>): boolean {
    const v = dayOption.value?.value;
    if (this.selectedDay?.value === v) {
      return true;
    }
    const fc = this.formControl?.value as Day | null;
    return fc?.value === v;
  }

  tabIndexForDay(dayOption: SelectOption<Day>): number {
    const selected = this.dayOptionsList.find((o) => this.isDaySelected(o));
    if (selected) {
      return dayOption === selected ? 0 : -1;
    }
    const first = this.dayOptionsList[0];
    return dayOption === first ? 0 : -1;
  }

  pickDay(day: Day) {
    if (!this.isMobile) {
      this.formControl.patchValue(day);
    }
    this.onDayPicked.emit(day);
  }

  onDayRadioKeydown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    const isRtl = this.dir?.value === 'rtl';
    let delta = 0;
    let handled = false;

    if (key === 'ArrowRight') {
      delta = isRtl ? -1 : 1;
      handled = true;
    } else if (key === 'ArrowLeft') {
      delta = isRtl ? 1 : -1;
      handled = true;
    } else if (key === 'ArrowDown') {
      delta = 1;
      handled = true;
    } else if (key === 'ArrowUp') {
      delta = -1;
      handled = true;
    } else if (key === 'Home') {
      event.preventDefault();
      this.pickDay(this.dayOptionsList[0].value);
      this.focusDayAt(0);
      return;
    } else if (key === 'End') {
      event.preventDefault();
      const last = this.dayOptionsList.length - 1;
      this.pickDay(this.dayOptionsList[last].value);
      this.focusDayAt(last);
      return;
    } else if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      this.pickDay(this.dayOptionsList[index].value);
      return;
    }

    if (handled && delta !== 0) {
      event.preventDefault();
      const len = this.dayOptionsList.length;
      const next = (index + delta + len) % len;
      this.pickDay(this.dayOptionsList[next].value);
      this.focusDayAt(next);
    }
  }

  private focusDayAt(index: number): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      this.dayRadioBtns?.get(index)?.nativeElement?.focus();
    });
  }
}
